import { Borrower, Loan, BorrowerTypeOption, LoanStatusOption, Asset } from '../types';

const API_BASE = '/api/v1';

// ============================================================================
// Borrower Types
// ============================================================================

export interface CreateBorrowerRequest {
  borrowerType: string;
  identificationNumber: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  department?: string;
  notes?: string;
}

export interface UpdateBorrowerRequest {
  borrowerType?: string;
  identificationNumber?: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  department?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export interface BorrowerFilters {
  borrowerType?: string;
  search?: string;
  isActive?: boolean;
}

export interface BorrowersListResponse {
  borrowers: Borrower[];
  total: number;
}

export interface BorrowerDetailResponse {
  borrower: Borrower & { loans: Loan[]; totalLoans: number };
}

// ============================================================================
// Loan Types
// ============================================================================

export interface LoanFilters {
  status?: string;
  borrowerId?: string;
  assetId?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
}

export interface LoansListResponse {
  loans: Loan[];
  total: number;
}

export interface LoanSummaryResponse {
  total: number;
  active: number;
  overdueCount: number;
  returned: number;
  byStatus: Record<string, number>;
}

export interface CheckoutRequest {
  assetId: string;
  borrowerId: string;
  dueDate: string;
  notes?: string;
}

export interface ReturnRequest {
  notes?: string;
}

export interface AvailableAsset {
  id: string;
  inventoryNumber: string;
  name: string;
  assetType: string;
  assetTypeLabel: string;
  siteName: string | null;
  roomName: string | null;
}

export interface AssetLoanHistoryResponse {
  asset: Asset;
  currentLoan: Loan | null;
  isCurrentlyLoaned: boolean;
  loanHistory: Loan[];
  totalLoans: number;
}

// Helper to format validation errors in Slovenian
function formatValidationErrors(details: Record<string, string>): string {
  const fieldLabels: Record<string, string> = {
    identificationNumber: 'ID stevilka',
    firstName: 'Ime',
    lastName: 'Priimek',
    borrowerType: 'Tip',
    email: 'Email',
    phone: 'Telefon',
    department: 'Oddelek',
  };

  return Object.entries(details)
    .map(([field]) => {
      const cleanField = field.replace(/[\[\]]/g, '');
      const label = fieldLabels[cleanField] || cleanField;
      return label + ': polje je obvezno';
    })
    .join(', ');
}

// ============================================================================
// Borrower API Functions
// ============================================================================

export async function fetchBorrowers(filters?: BorrowerFilters): Promise<BorrowersListResponse> {
  const params = new URLSearchParams();
  if (filters?.borrowerType) params.append('borrowerType', filters.borrowerType);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));

  const url = params.toString() ? `${API_BASE}/borrowers?${params}` : `${API_BASE}/borrowers`;
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch borrowers');
  }
  return response.json();
}

export async function fetchBorrower(id: string): Promise<BorrowerDetailResponse> {
  const response = await fetch(`${API_BASE}/borrowers/${id}`, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch borrower');
  }
  return response.json();
}

export async function fetchBorrowerTypes(): Promise<{ types: BorrowerTypeOption[] }> {
  const response = await fetch(`${API_BASE}/borrowers/types`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch borrower types');
  return response.json();
}

export async function createBorrower(data: CreateBorrowerRequest): Promise<{ success: boolean; borrower: Borrower }> {
  const response = await fetch(`${API_BASE}/borrowers`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    if (error.details) {
      throw new Error(formatValidationErrors(error.details));
    }
    throw new Error(error.message || error.error || 'Failed to create borrower');
  }
  return response.json();
}

export async function updateBorrower(id: string, data: UpdateBorrowerRequest): Promise<{ success: boolean; borrower: Borrower }> {
  const response = await fetch(`${API_BASE}/borrowers/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to update borrower');
  }
  return response.json();
}

export async function deleteBorrower(id: string): Promise<{ success: boolean; message: string; deactivated?: boolean }> {
  const response = await fetch(`${API_BASE}/borrowers/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to delete borrower');
  }
  return response.json();
}

// ============================================================================
// Loan API Functions
// ============================================================================

export async function fetchLoans(filters?: LoanFilters): Promise<LoansListResponse> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.borrowerId) params.append('borrowerId', filters.borrowerId);
  if (filters?.assetId) params.append('assetId', filters.assetId);
  if (filters?.fromDate) params.append('fromDate', filters.fromDate);
  if (filters?.toDate) params.append('toDate', filters.toDate);
  if (filters?.search) params.append('search', filters.search);

  const url = params.toString() ? `${API_BASE}/loans?${params}` : `${API_BASE}/loans`;
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch loans');
  }
  return response.json();
}

export async function fetchActiveLoan(): Promise<LoansListResponse> {
  const response = await fetch(`${API_BASE}/loans/active`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch active loans');
  return response.json();
}

export async function fetchOverdueLoans(): Promise<LoansListResponse & { count: number }> {
  const response = await fetch(`${API_BASE}/loans/overdue`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch overdue loans');
  return response.json();
}

export async function fetchLoanSummary(): Promise<LoanSummaryResponse> {
  const response = await fetch(`${API_BASE}/loans/summary`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch loan summary');
  return response.json();
}

export async function fetchLoanStatuses(): Promise<{ statuses: LoanStatusOption[] }> {
  const response = await fetch(`${API_BASE}/loans/statuses`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch loan statuses');
  return response.json();
}

export async function fetchAvailableAssets(): Promise<{ assets: AvailableAsset[]; total: number }> {
  const response = await fetch(`${API_BASE}/loans/available-assets`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch available assets');
  return response.json();
}

export async function checkoutAsset(data: CheckoutRequest): Promise<{ success: boolean; loan: Loan; message: string }> {
  const response = await fetch(`${API_BASE}/loans/checkout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to checkout asset');
  }
  return response.json();
}

export async function returnAsset(loanId: string, notes?: string): Promise<{ success: boolean; loan: Loan; message: string }> {
  const response = await fetch(`${API_BASE}/loans/${loanId}/return`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to return asset');
  }
  return response.json();
}

export async function updateLoan(loanId: string, data: { dueDate?: string; notes?: string }): Promise<{ success: boolean; loan: Loan }> {
  const response = await fetch(`${API_BASE}/loans/${loanId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to update loan');
  }
  return response.json();
}

export async function fetchAssetLoanHistory(assetId: string): Promise<AssetLoanHistoryResponse> {
  const response = await fetch(`${API_BASE}/assets/${assetId}/loans`, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch asset loan history');
  }
  return response.json();
}
